import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "universal-cookie";
import { handleResponseCode } from "../../../app/utilities/helpers";
import GetProxy from "../../../app/GetProxy";

const cookies = new Cookies();
const proxy = GetProxy();

const STATUS_API_TO_UI = {
  PENDING_APPROVAL: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const ASSET_TYPE_API_TO_UI = {
  SERVER: "Server",
  FIREWALL: "Firewall",
  NETWORK: "Network",
  OTHER: "Other",
  OTHERS: "Others",
};

const NETWORK_API_TO_UI = {
  IT_ENTERPRISE: "IT Enterprise",
  TELCO: "Telco",
};

const transformAssetItem = (item) => {
  const apiStatus = item.status;
  const isRejected = apiStatus === "REJECTED";
  const isApproved = apiStatus === "APPROVED";
  return {
    id: item.id,
    dccName: item.dccName || "",
    hostName: item.hostName || "",
    ipAddress: item.ip || "",
    assetType: ASSET_TYPE_API_TO_UI[item.assetType] || item.assetType || "",
    location: item.location || "",
    environment: item.environment || "",
    network: NETWORK_API_TO_UI[item.network] || item.network || "",
    scanType: item.scanType || "",
    assetMode: item.assetMode || "",
    status: STATUS_API_TO_UI[apiStatus] || apiStatus || "",
    // Pending tab info popup
    createdBy: item.createdBy || "",
    createdOn: item.createdDt || "",
    updatedBy: item.updatedBy || "",
    updatedOn: item.updateDt || "",
    // Approved/Rejected tab info popup
    requestedBy: item.requestedBy || "",
    requestedOn: item.requestedOn || "",
    approvedRejectedBy: isRejected
      ? item.rejectedBy || ""
      : item.approvedBy || "",
    approvedRejectedOn: isApproved || isRejected ? item.decidedOn || "" : "",
    comment: item.comment || item.rejectionReason || "",
  };
};

export const transformAssetToFormValues = (item) => ({
  dccName: item?.dccName || "",
  hostName: item?.hostName || "",
  ipAddress: item?.ip || "",
  assetType: ASSET_TYPE_API_TO_UI[item?.assetType] || item?.assetType || "",
  location: item?.location || "",
  environment: item?.environment || "",
  network: NETWORK_API_TO_UI[item?.network] || item?.network || "",
  scanType: item?.scanType || "",
  assetMode: item?.assetMode || "AGENT_BASED",
});

export const getAssetDetailsById = createAsyncThunk(
  "get/getAssetDetailsById",
  async (id) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(`${proxy}/sassp/assets/details/${id}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
      });
      handleResponseCode(response);
      if (!response.ok) {
        throw new Error("Failed to fetch asset details");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

export const getAssetList = createAsyncThunk(
  "post/getAssetList",
  async ({ pageNumber, itemsPerPage, payload }) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(
        `${proxy}/sassp/assets/list/${pageNumber}/${itemsPerPage}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "DTOP-API-TOKEN": apiToken,
            USERNAME: username,
          },
          body: JSON.stringify(payload),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

export const saveAsset = createAsyncThunk("post/saveAsset", async (payload) => {
  const apiToken = cookies.get("apitoken");
  const username = cookies.get("username");
  try {
    const response = await fetch(`${proxy}/sassp/assets/save`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "DTOP-API-TOKEN": apiToken,
        USERNAME: username,
      },
      body: JSON.stringify(payload),
    });
    handleResponseCode(response);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
});

export const importAssets = createAsyncThunk(
  "post/importAssets",
  async (file, { rejectWithValue }) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${proxy}/sassp/assets/import`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
        body: formData,
      });
      handleResponseCode(response);
      const rawText = await response.text();
      let data;
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch (e) {
        data = rawText;
      }
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

// A row the import skipped as a duplicate. It is not an asset yet — it is the
// CSV line as it was read, plus the backend's reason for holding it back.
export const transformImportDuplicate = (item) => ({
  id: item.id,
  row: item.row,
  dccName: item.dccName || "",
  hostName: item.hostName || "",
  ipAddress: item.ip || "",
  assetType: ASSET_TYPE_API_TO_UI[item.assetType] || item.assetType || "",
  location: item.location || "",
  environment: item.environment || "",
  network: NETWORK_API_TO_UI[item.network] || item.network || "",
  scanType: item.scanType || "",
  assetMode: item.assetMode || "",
  reason: item.reason || "",
  resolved: !!item.resolved,
});

// Step 2 of the import: the rows the upload skipped as duplicates. Returned in
// full — the backend does not paginate this one.
export const getImportDuplicates = createAsyncThunk(
  "get/getImportDuplicates",
  async (importId) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(
        `${proxy}/sassp/assets/import/${importId}/duplicates`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "DTOP-API-TOKEN": apiToken,
            USERNAME: username,
          },
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

// Step 3: save the skipped rows the user ticked, duplicate check overridden.
export const saveImportDuplicates = createAsyncThunk(
  "post/saveImportDuplicates",
  async ({ importId, ids }) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(
        `${proxy}/sassp/assets/import/${importId}/duplicates/save`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "DTOP-API-TOKEN": apiToken,
            USERNAME: username,
          },
          body: JSON.stringify({ ids }),
        },
      );
      handleResponseCode(response);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

export const getAssetConfigList = createAsyncThunk(
  "get/getAssetConfigList",
  async () => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(`${proxy}/sassp/assets/configList`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
      });
      handleResponseCode(response);
      if (!response.ok) {
        throw new Error("Failed to fetch config list");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

export const downloadAssetImportTemplate = createAsyncThunk(
  "get/downloadAssetImportTemplate",
  async () => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(`${proxy}/sassp/assets/import/template`, {
        method: "GET",
        headers: {
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
      });
      handleResponseCode(response);
      if (!response.ok) {
        throw new Error("Failed to download template");
      }
      const contentDisposition =
        response.headers.get("Content-Disposition") || "";
      const fileNameMatch = contentDisposition.match(
        /filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i,
      );
      const fileName = fileNameMatch
        ? decodeURIComponent(fileNameMatch[1])
        : "asset_import_template.csv";
      const blob = await response.blob();
      return { blob, fileName };
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

export const updateAsset = createAsyncThunk(
  "post/updateAsset",
  async (payload) => {
    const apiToken = cookies.get("apitoken");
    const username = cookies.get("username");
    try {
      const response = await fetch(`${proxy}/sassp/assets/update`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "DTOP-API-TOKEN": apiToken,
          USERNAME: username,
        },
        body: JSON.stringify(payload),
      });
      handleResponseCode(response);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  },
);

const initialState = {
  assetListData: {
    data: [],
    totalCount: 0,
  },
  assetListLoading: false,
  // Whichever list request went out last. Switching tabs fires a new one
  // while the previous tab's is still in flight, and the two can come back
  // out of order — without this the slower, older response would land on
  // top of the tab the user is now looking at.
  assetListRequestId: "",
  saveAssetLoading: false,
  deleteAssetLoading: false,
  importAssetLoading: false,
  importDuplicatesLoading: false,
  saveDuplicatesLoading: false,
  assetDetailsLoading: false,
  assetConfigList: {
    assetType: [],
    location: [],
    environment: [],
    network: [],
    scanType: [],
  },
  assetConfigLoading: false,
  error: "",
  apiError: false,
};

export const AssetOnboardingSlice = createSlice({
  name: "AssetOnboardingSlice",
  initialState,
  reducers: {
    setErrorAlert(state) {
      state.apiError = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAssetList.pending, (state, action) => {
        state.assetListRequestId = action.meta?.requestId ?? "";
        state.assetListLoading = true;
      })
      .addCase(getAssetList.fulfilled, (state, action) => {
        // A superseded request: its rows belong to a tab or a page the
        // user has already moved off.
        if (state.assetListRequestId !== (action.meta?.requestId ?? "")) {
          return;
        }
        state.assetListLoading = false;
        const items = (action.payload?.data || []).map(transformAssetItem);
        state.assetListData = {
          data: items,
          totalCount: action.payload?.totalCount ?? items.length,
        };
      })
      .addCase(getAssetList.rejected, (state, action) => {
        if (state.assetListRequestId !== (action.meta?.requestId ?? "")) {
          return;
        }
        state.assetListLoading = false;
        state.assetListData = { data: [], totalCount: 0 };
        state.error = action.error?.message || "Failed to load assets";
        state.apiError = true;
      })
      .addCase(saveAsset.pending, (state) => {
        state.saveAssetLoading = true;
      })
      .addCase(saveAsset.fulfilled, (state) => {
        state.saveAssetLoading = false;
      })
      .addCase(saveAsset.rejected, (state, action) => {
        state.saveAssetLoading = false;
        state.error = action.error?.message || "Failed to save asset";
        state.apiError = true;
      })
      .addCase(updateAsset.pending, (state) => {
        state.saveAssetLoading = true;
      })
      .addCase(updateAsset.fulfilled, (state) => {
        state.saveAssetLoading = false;
      })
      .addCase(updateAsset.rejected, (state, action) => {
        state.saveAssetLoading = false;
        state.error = action.error?.message || "Failed to update asset";
        state.apiError = true;
      })
      .addCase(getAssetDetailsById.pending, (state) => {
        state.assetDetailsLoading = true;
      })
      .addCase(getAssetDetailsById.fulfilled, (state) => {
        state.assetDetailsLoading = false;
      })
      .addCase(getAssetDetailsById.rejected, (state, action) => {
        state.assetDetailsLoading = false;
        state.error = action.error?.message || "Failed to load asset details";
        state.apiError = true;
      })
      .addCase(importAssets.pending, (state) => {
        state.importAssetLoading = true;
      })
      .addCase(importAssets.fulfilled, (state) => {
        state.importAssetLoading = false;
      })
      .addCase(importAssets.rejected, (state, action) => {
        state.importAssetLoading = false;
        state.error =
          (typeof action.payload === "string"
            ? action.payload
            : action.payload?.message) ||
          action.error?.message ||
          "Failed to import assets";
        state.apiError = true;
      })
      .addCase(getImportDuplicates.pending, (state) => {
        state.importDuplicatesLoading = true;
      })
      .addCase(getImportDuplicates.fulfilled, (state) => {
        state.importDuplicatesLoading = false;
      })
      .addCase(getImportDuplicates.rejected, (state, action) => {
        state.importDuplicatesLoading = false;
        state.error =
          action.error?.message || "Failed to load duplicate records";
        state.apiError = true;
      })
      .addCase(saveImportDuplicates.pending, (state) => {
        state.saveDuplicatesLoading = true;
      })
      .addCase(saveImportDuplicates.fulfilled, (state) => {
        state.saveDuplicatesLoading = false;
      })
      .addCase(saveImportDuplicates.rejected, (state, action) => {
        state.saveDuplicatesLoading = false;
        state.error =
          action.error?.message || "Failed to save duplicate records";
        state.apiError = true;
      })
      .addCase(getAssetConfigList.pending, (state) => {
        state.assetConfigLoading = true;
      })
      .addCase(getAssetConfigList.fulfilled, (state, action) => {
        state.assetConfigLoading = false;
        const toOpts = (arr) =>
          (arr || []).map((v) => ({ value: v, label: v }));
        const toLabelOpts = (arr, map) =>
          (arr || []).map((v) => {
            const label = map[v] || v;
            return { value: label, label };
          });
        state.assetConfigList = {
          assetType: toLabelOpts(
            action.payload?.assetType,
            ASSET_TYPE_API_TO_UI,
          ),
          location: toOpts(action.payload?.location),
          environment: toOpts(action.payload?.environment),
          network: toLabelOpts(action.payload?.network, NETWORK_API_TO_UI),
          scanType: toOpts(action.payload?.typeOfScan),
        };
      })
      .addCase(getAssetConfigList.rejected, (state, action) => {
        state.assetConfigLoading = false;
        state.error = action.error?.message || "Failed to load asset config";
        state.apiError = true;
      });
  },
});

export const { setErrorAlert } = AssetOnboardingSlice.actions;
export default AssetOnboardingSlice.reducer;
